// src/pages/AISentryLight.jsx
// AI-Enhanced Network Topology - Light Mode for Presentations
// Powered by AI Sentry - With Nimbus Integration for Autonomous Actions

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { edgeNodesService } from '../services/edge-nodes';
import { eventsService } from '../services/events';
import { sendChatQuery } from '../services/chat';
import api from '../services/api';

// ════════════════════════════════════════════════════════════════════════════
// MARKDOWN STYLING COMPONENTS - LIGHT MODE
// ════════════════════════════════════════════════════════════════════════════

const MarkdownComponents = {
  h1: ({ children }) => <h1 className="text-lg font-bold text-slate-800 mb-3 mt-4 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold text-cyan-700 mb-2 mt-4 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-cyan-600 mb-2 mt-3">{children}</h3>,
  p: ({ children }) => <p className="text-slate-700 text-sm mb-2 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="text-slate-900 font-semibold">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc list-inside text-slate-700 text-sm mb-2 space-y-1 ml-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside text-slate-700 text-sm mb-2 space-y-1 ml-2">{children}</ol>,
  li: ({ children }) => <li className="text-slate-700">{children}</li>,
  hr: () => <hr className="border-slate-300 my-3" />,
  table: ({ children }) => (
    <div className="overflow-x-auto mb-3 rounded-lg border border-slate-300">
      <table className="min-w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-100">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-slate-200">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-slate-50">{children}</tr>,
  th: ({ children }) => <th className="px-2 py-1.5 text-left font-semibold text-cyan-700 whitespace-nowrap">{children}</th>,
  td: ({ children }) => <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap">{children}</td>,
  code: ({ children }) => <code className="bg-slate-100 px-1 py-0.5 rounded text-cyan-700 text-xs font-mono border border-slate-200">{children}</code>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-cyan-500 pl-3 my-2 text-slate-600 italic">{children}</blockquote>,
};

// ════════════════════════════════════════════════════════════════════════════
// AI STATUS TYPES
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS = {
  ISOLATED: { icon: '🛡️', text: 'ISOLATED', bg: 'bg-red-500', glow: 'from-red-500/30 to-rose-500/30' },
  ANALYZING: { icon: '🔍', text: 'ANALYZING', bg: 'bg-purple-500', glow: 'from-purple-500/30 to-violet-500/30', pulse: true },
  ALERT: { icon: '⚠️', text: 'ALERT', bg: 'bg-yellow-500', glow: 'from-yellow-500/30 to-amber-500/30' },
  PROTECTED: { icon: '🤖', text: 'PROTECTED', bg: 'bg-emerald-500', glow: 'from-emerald-500/20 to-teal-500/20' },
};

// ════════════════════════════════════════════════════════════════════════════
// NIMBUS API INTEGRATION
// ════════════════════════════════════════════════════════════════════════════

const executeNimbusAction = async (actionType, params) => {
  try {
    const response = await api.post('/nimbus/action/', {
      action_type: actionType,
      params: params
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Nimbus action failed:', error);
    return { success: false, error: error.message };
  }
};

// ════════════════════════════════════════════════════════════════════════════
// HEURISTIC DETECTION
// ════════════════════════════════════════════════════════════════════════════

const detectThreatsHeuristic = (events, edgeNodes) => {
  const threats = [];
  const bySource = {};
  
  events.forEach(event => {
    const sourceIp = event.source_ip;
    const destIp = event.destination_ip;
    const severity = event.severity;
    const policyName = event.details?.policy_name || '';
    
    if (!sourceIp || sourceIp === 'N/A' || !destIp || destIp === 'N/A') return;
    
    if (!bySource[sourceIp]) {
      bySource[sourceIp] = { targets: new Set(), criticalCount: 0, policies: new Set(), events: [], agentId: null };
    }
    
    bySource[sourceIp].targets.add(destIp);
    bySource[sourceIp].events.push(event);
    if (policyName) bySource[sourceIp].policies.add(policyName);
    if (severity === 'Critical') bySource[sourceIp].criticalCount++;
    if (event.celerway_edge_node_id) bySource[sourceIp].agentId = event.celerway_edge_node_id;
  });

  Object.entries(bySource).forEach(([sourceIp, data]) => {
    const policies = Array.from(data.policies);
    const hasUnauthorized = policies.some(p => 
      p.toLowerCase().includes('unauthorized') || p.toLowerCase().includes('critical infrastructure')
    );
    
    if (data.criticalCount > 0 && hasUnauthorized) {
      threats.push({
        id: `isolation-${sourceIp}`,
        type: 'ISOLATION',
        device: sourceIp,
        targetDevices: Array.from(data.targets),
        reason: 'Unauthorized access blocked',
        confidence: Math.min(98, 85 + data.criticalCount * 3),
        severity: 'critical',
        agentId: data.agentId,
      });
      
      if (data.agentId) {
        threats.push({
          id: `response-${sourceIp}`,
          type: 'VPN_ACTION',
          device: sourceIp,
          targetDevices: Array.from(data.targets),
          reason: 'Recommend network isolation response',
          confidence: 95,
          severity: 'critical',
          agentId: data.agentId,
          actionDetails: {
            actionType: 'Tag Assignment',
            description: 'Apply security tag to router via Nimbus API',
            tagPrefix: 'aisentry-isolated',
            visibleIn: 'Nimbus Portal'
          }
        });
      }
    } else if (data.targets.size >= 3 && data.events.length >= 10) {
      threats.push({
        id: `scan-${sourceIp}`,
        type: 'RECOMMENDATION',
        device: sourceIp,
        targetDevices: Array.from(data.targets),
        reason: 'Port scan pattern detected',
        confidence: Math.min(90, 70 + data.targets.size * 4),
        severity: 'high',
        agentId: data.agentId,
      });
    }
  });

  return threats.sort((a, b) => {
    const sevOrder = { critical: 0, high: 1, medium: 2 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

const getDeviceAIStatus = (deviceIp, threats) => {
  const asSource = threats.find(t => t.device === deviceIp);
  if (asSource) {
    if (asSource.type === 'ISOLATION') return AI_STATUS.ISOLATED;
    if (asSource.type === 'RECOMMENDATION') return AI_STATUS.ALERT;
  }
  const asTarget = threats.find(t => t.targetDevices?.includes(deviceIp));
  if (asTarget && asTarget.type === 'ISOLATION') return AI_STATUS.ANALYZING;
  return AI_STATUS.PROTECTED;
};

// ════════════════════════════════════════════════════════════════════════════
// AI Sentry API CALLS
// ════════════════════════════════════════════════════════════════════════════

const generateAIBriefing = async () => {
  try {
    const prompt = `Generate a brief shift change security briefing for Brian (2-3 sentences max). 
Start with "Attention needed:" if there are critical threats, or "All clear:" if the network is secure.
Focus on the most important security concern right now. Be direct and actionable.`;

    const response = await api.post('/chat/', {
      message: prompt,
      history: [],
      session_id: `briefing-${Date.now()}`
    });
    
    const content = response.data.content;
    if (content && content.includes('encountered an error')) return null;
    return content || null;
  } catch (error) {
    console.log('AI briefing error:', error.message);
    return null;
  }
};

const generateExpandedBriefing = async () => {
  try {
    const prompt = `Generate a detailed security analysis report. Include:
1. **Threat Summary**: What attacks or suspicious activity are happening?
2. **Affected Assets**: Which PLCs, devices, or network segments are involved?
3. **Attack Timeline**: When did this activity start and what's the pattern?
4. **Risk Assessment**: How critical is this situation?
5. **Recommended Actions**: What should Brian do right now?

Be specific with IP addresses, protocols, and timestamps from the event data.`;

    const response = await api.post('/chat/', {
      message: prompt,
      history: [],
      session_id: `expanded-${Date.now()}`
    });
    
    const content = response.data.content;
    if (content && content.includes('encountered an error')) return null;
    return content || null;
  } catch (error) {
    console.log('Expanded briefing error:', error.message);
    return null;
  }
};

// ════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ════════════════════════════════════════════════════════════════════════════

const isNodeOnline = (node) => {
  if (!node.last_heartbeat) return false;
  
  let heartbeatStr = node.last_heartbeat;
  if (!heartbeatStr.endsWith('Z') && !heartbeatStr.includes('+')) {
    heartbeatStr += 'Z';
  }
  
  const lastHeartbeat = new Date(heartbeatStr).getTime();
  const nowUTC = Date.now();
  const diffMinutes = (nowUTC - lastHeartbeat) / (1000 * 60);
  
  return diffMinutes <= 15;
};

const getNodeStatus = (node) => {
  return isNodeOnline(node) ? 'Online' : 'Offline';
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS - LIGHT MODE
// ════════════════════════════════════════════════════════════════════════════

const CloudNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-500 !border-2 !border-white" />
    <div className="relative bg-white border-2 border-cyan-400 rounded-2xl p-6 min-w-[200px] shadow-xl"
         style={{ boxShadow: '0 4px 20px rgba(6, 182, 212, 0.2)' }}>
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500 shadow-md" />
      </div>
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl flex items-center justify-center border border-cyan-300">
          <svg className="w-8 h-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        </div>
      </div>
      <div className="text-center">
        <div className="text-sm font-semibold text-slate-800">{data.label}</div>
        <div className="text-xs text-slate-500 mt-1">{data.sublabel}</div>
      </div>
      <div className="mt-4 pt-3 border-t border-slate-200 flex justify-center gap-4">
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-600">{data.edgeCount || 0}</div>
          <div className="text-[10px] text-slate-500 uppercase">Agents</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-600">{data.assetCount || 0}</div>
          <div className="text-[10px] text-slate-500 uppercase">Devices</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-center gap-2">
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-[10px] text-emerald-600 font-medium">AI Sentry Active</span>
      </div>
    </div>
  </div>
);

const EdgeGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-blue-500 !border-2 !border-white" />
      
      <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden min-w-[280px] transition-all group-hover:shadow-2xl"
           style={{ boxShadow: '0 4px 25px rgba(0, 0, 0, 0.1)' }}>
        
        {/* Orange Header - Celerway Style */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-400 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">C</span>
              </div>
              <div>
                <div className="text-white font-bold text-sm">CELERWAY STRATUS 5G</div>
                <div className="text-white/80 text-[10px]">SASE Edge Gateway</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {isOnline && <span className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse" />}
              <span className={`text-xs font-semibold ${isOnline ? 'text-emerald-200' : 'text-red-200'}`}>
                {isOnline ? 'ONLINE' : 'OFFLINE'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="p-4 space-y-3">
          {/* Palo Alto SASE Section */}
          <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-[9px] font-bold rounded border border-orange-200">
                PALO ALTO NETWORKS
              </span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <div className="text-slate-800 font-semibold text-sm">Prisma SASE</div>
                <div className="text-slate-500 text-[10px]">Cloud Security Service</div>
              </div>
            </div>
            {/* Compliance Badges */}
            <div className="flex flex-wrap gap-1">
              <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[8px] font-semibold rounded border border-emerald-200">PCI-DSS</span>
              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[8px] font-semibold rounded border border-blue-200">SOX</span>
              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[8px] font-semibold rounded border border-purple-200">Zero Trust</span>
              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[8px] font-semibold rounded border border-red-200">Threat Prevention</span>
            </div>
          </div>
          
          {/* AI Sentry Section */}
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-3 border border-cyan-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm">🤖</span>
              </div>
              <div>
                <div className="text-slate-800 font-semibold text-sm">AI Sentry</div>
                <div className="text-slate-500 text-[10px]">Deep Packet • OT Polling</div>
              </div>
              <div className="ml-auto flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-[9px] text-emerald-600 font-medium">Active</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              <span className="px-1.5 py-0.5 bg-cyan-100 text-cyan-700 text-[8px] font-semibold rounded border border-cyan-200">Modbus</span>
              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[8px] font-semibold rounded border border-purple-200">OPC-UA</span>
              <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[8px] font-semibold rounded border border-orange-200">S7</span>
              <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 text-[8px] font-semibold rounded border border-slate-200">DNP3</span>
            </div>
          </div>
          
          {/* Connectivity Cards */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-50 rounded-lg p-2 border border-slate-200">
              <div className="flex items-center gap-1.5 mb-1">
                <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                </svg>
                <span className="text-slate-700 text-[10px] font-semibold">5G/LTE</span>
              </div>
              <div className="text-slate-500 text-[9px]">Multi-carrier bonding</div>
              <div className="text-blue-600 text-[10px] font-bold">↑ 85 Mbps</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2 border border-slate-200">
              <div className="flex items-center gap-1.5 mb-1">
                <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-slate-700 text-[10px] font-semibold">IPSec VPN</span>
              </div>
              <div className="text-slate-500 text-[9px]">AES-256-GCM</div>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                <span className="text-emerald-600 text-[10px] font-bold">Active</span>
              </div>
            </div>
          </div>
          
          {/* Air-Gapped Badge */}
          <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-200 flex items-center gap-2">
            <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <div className="text-emerald-800 text-[11px] font-semibold">Air-Gapped from Local LAN</div>
              <div className="text-emerald-600 text-[9px]">OT traffic never touches enterprise network</div>
            </div>
          </div>
          
          {/* Footer Stats */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
            <div className="text-[9px] text-slate-500">
              {data.nodeId}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <div className="text-blue-600 font-bold text-sm">{data.deviceCount || 0}</div>
                <div className="text-[8px] text-slate-500 uppercase">Devices</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeviceNode = ({ data }) => {
  const protocolColors = {
    modbus: { bg: 'bg-orange-100', text: 'text-orange-600', badge: 'bg-orange-100 text-orange-700 border-orange-300' },
    opcua: { bg: 'bg-purple-100', text: 'text-purple-600', badge: 'bg-purple-100 text-purple-700 border-purple-300' },
    tcp: { bg: 'bg-blue-100', text: 'text-blue-600', badge: 'bg-blue-100 text-blue-700 border-blue-300' },
    default: { bg: 'bg-slate-100', text: 'text-slate-600', badge: 'bg-slate-100 text-slate-700 border-slate-300' },
  };
  
  const primaryProtocol = data.protocols?.[0] || 'default';
  const colors = protocolColors[primaryProtocol] || protocolColors.default;
  const aiStatus = data.aiStatus || AI_STATUS.PROTECTED;
  const hasViolations = data.policyViolations > 0;
  
  const getBorderClass = () => {
    if (aiStatus === AI_STATUS.ISOLATED) return 'border-red-500 border-2';
    if (aiStatus === AI_STATUS.ANALYZING) return 'border-purple-500 border-2';
    if (aiStatus === AI_STATUS.ALERT) return 'border-yellow-500 border-2';
    if (hasViolations) return 'border-red-400';
    return 'border-slate-200';
  };

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-orange-500 !border-2 !border-white" />
      
      <div className={`relative bg-white ${getBorderClass()} rounded-lg p-3 min-w-[160px] shadow-md transition-all group-hover:shadow-lg ${aiStatus.pulse ? 'animate-pulse' : ''}`}
           style={{ boxShadow: aiStatus === AI_STATUS.ISOLATED ? '0 4px 20px rgba(239, 68, 68, 0.2)' : '0 4px 15px rgba(0, 0, 0, 0.08)' }}>
        
        <div className={`absolute -top-2 -right-2 px-2 py-0.5 ${aiStatus.bg} text-white text-[9px] font-bold rounded-full shadow-md flex items-center gap-1`}>
          <span>{aiStatus.icon}</span>
          <span>{aiStatus.text}</span>
        </div>
        
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.bg}`}>
            <svg className={`w-4 h-4 ${colors.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-800">{data.role || 'Device'}</div>
            <div className="text-[9px] text-slate-500">DISCOVERED</div>
          </div>
        </div>
        
        <div className="bg-slate-50 rounded px-2 py-1.5 mb-2 border border-slate-100">
          <div className={`text-sm font-mono font-semibold ${
            aiStatus === AI_STATUS.ISOLATED ? 'text-red-600' :
            aiStatus === AI_STATUS.ALERT ? 'text-yellow-600' : 'text-cyan-600'
          }`}>
            {data.ip}
          </div>
        </div>
        
        {data.protocols && data.protocols.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {data.protocols.map((protocol, idx) => {
              const pColor = protocolColors[protocol] || protocolColors.default;
              return (
                <span key={idx} className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold uppercase ${pColor.badge}`}>
                  {protocol}
                </span>
              );
            })}
          </div>
        )}
        
        <div className="text-[10px] text-slate-500 text-center">
          {data.eventCount || 0} events
        </div>
      </div>
    </div>
  );
};

const nodeTypes = { cloud: CloudNode, edgeGateway: EdgeGatewayNode, device: DeviceNode };

// ════════════════════════════════════════════════════════════════════════════
// SHIFT BRIEFING MODAL - LIGHT MODE
// ════════════════════════════════════════════════════════════════════════════

const ShiftBriefingModal = ({ isOpen, onClose, aiBriefing, stats, threats, isLoadingAI, onRequestExpanded, expandedBriefing, isLoadingExpanded }) => {
  const [showExpanded, setShowExpanded] = useState(false);
  
  if (!isOpen) return null;
  
  const isolatedCount = threats.filter(t => t.type === 'ISOLATION').length;
  const hasCritical = isolatedCount > 0;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-slate-200 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`bg-gradient-to-r ${hasCritical ? 'from-red-500 to-orange-500' : 'from-cyan-500 to-blue-500'} px-6 py-4 flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <span className="text-2xl">{hasCritical ? '🚨' : '🤖'}</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Shift Briefing</h2>
              <p className="text-white/80 text-sm">AI Sentry Security Summary</p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
              <div className="text-2xl font-bold text-emerald-600">{stats.totalDevices || 0}</div>
              <div className="text-xs text-slate-500">Devices</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
              <div className="text-2xl font-bold text-red-600">{isolatedCount}</div>
              <div className="text-xs text-slate-500">Threats</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
              <div className="text-2xl font-bold text-blue-600">{stats.alertCount || 0}</div>
              <div className="text-xs text-slate-500">Events</div>
            </div>
          </div>
          
          {/* AI Briefing */}
          <div className={`rounded-xl p-4 ${hasCritical ? 'bg-red-50 border border-red-200' : 'bg-slate-50 border border-slate-200'}`}>
            {isLoadingAI ? (
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-cyan-300 border-t-cyan-600 rounded-full animate-spin" />
                <span className="text-slate-600 text-sm">AI Sentry is analyzing your network...</span>
              </div>
            ) : aiBriefing ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🧠</span>
                  <span className="text-sm font-semibold text-cyan-700">AI Analysis</span>
                </div>
                <p className="text-slate-800 text-sm leading-relaxed">{aiBriefing}</p>
              </>
            ) : (
              <>
                <p className="text-slate-800 font-medium mb-2">
                  {hasCritical ? '⚠️ Attention needed:' : '✅ Status:'}
                </p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {hasCritical 
                    ? `Detected ${isolatedCount} threat source${isolatedCount > 1 ? 's' : ''} attempting unauthorized access.`
                    : `All ${stats.totalDevices || 0} devices operating normally.`
                  }
                </p>
              </>
            )}
          </div>
          
          {/* Expanded Briefing Toggle */}
          {aiBriefing && (
            <div>
              {!showExpanded ? (
                <button
                  onClick={() => {
                    setShowExpanded(true);
                    if (!expandedBriefing) onRequestExpanded();
                  }}
                  className="w-full py-2 text-sm text-cyan-600 hover:text-cyan-700 flex items-center justify-center gap-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors border border-slate-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Show Expanded Analysis
                </button>
              ) : (
                <div className="bg-slate-50 rounded-xl p-4 border border-cyan-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">📊</span>
                      <span className="text-sm font-semibold text-cyan-700">Expanded Analysis</span>
                    </div>
                    <button onClick={() => setShowExpanded(false)} className="text-slate-500 hover:text-slate-700 text-xs">
                      Collapse ▲
                    </button>
                  </div>
                  {isLoadingExpanded ? (
                    <div className="flex items-center gap-3 py-4">
                      <div className="w-5 h-5 border-2 border-cyan-300 border-t-cyan-600 rounded-full animate-spin" />
                      <span className="text-slate-600 text-sm">Generating detailed analysis...</span>
                    </div>
                  ) : expandedBriefing ? (
                    <div className="max-h-80 overflow-y-auto pr-2">
                      <ReactMarkdown components={MarkdownComponents}>
                        {expandedBriefing}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">Unable to generate expanded analysis.</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Threat List */}
          {threats.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-500 mb-2">Detected Threats</h3>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {threats.slice(0, 3).map(threat => (
                  <div key={threat.id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2 border border-slate-200">
                    <span className="text-lg">{threat.type === 'ISOLATION' ? '🛡️' : '⚠️'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800 truncate">{threat.reason}</div>
                      <div className="text-xs text-slate-500">{threat.device} → {threat.targetDevices?.length || 0} target(s)</div>
                    </div>
                    <div className="text-xs text-slate-400">{threat.confidence}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-md flex items-center justify-center">
              <span className="text-xs font-bold text-white">e</span>
            </div>
            <span className="text-xs text-slate-500">Powered by <span className="text-cyan-600 font-semibold">AI Sentry</span></span>
          </div>
          <button onClick={onClose} className={`px-6 py-2 ${hasCritical ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600' : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600'} text-white font-semibold rounded-lg transition-all`}>
            {hasCritical ? 'Review Threats' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT - LIGHT MODE
// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// AI PANEL - LIGHT MODE
// ════════════════════════════════════════════════════════════════════════════

const AIPanel = ({ isOpen, onClose, threats, recentEvents, onRefresh, isRefreshing, onApproveVPN }) => {
  const [activeTab, setActiveTab] = useState('detections');
  const [executingAction, setExecutingAction] = useState(null);
  const [actionResults, setActionResults] = useState({});
  
  if (!isOpen) return null;
  
  const criticalEvents = recentEvents?.filter(e => e.severity === 'Critical').slice(0, 10) || [];
  const warningEvents = recentEvents?.filter(e => e.severity === 'Warning').slice(0, 10) || [];
  
  const vpnActions = threats.filter(t => t.type === 'VPN_ACTION');
  const otherThreats = threats.filter(t => t.type !== 'VPN_ACTION');
  
  const handleApproveVPN = async (action) => {
    setExecutingAction(action.id);
    try {
      const result = await onApproveVPN(action);
      setActionResults(prev => ({ ...prev, [action.id]: result }));
    } catch (error) {
      setActionResults(prev => ({ ...prev, [action.id]: { success: false, error: error.message } }));
    } finally {
      setExecutingAction(null);
    }
  };
  
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-[420px] bg-white border-l border-slate-200 z-50 shadow-2xl overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-500 to-blue-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xl">🤖</span>
            <div>
              <h2 className="text-white font-bold">AI Sentry Activity</h2>
              <p className="text-white/80 text-xs">Real-time threat detection</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-slate-200 flex-shrink-0">
          <button
            onClick={() => setActiveTab('detections')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'detections' 
                ? 'text-cyan-600 border-b-2 border-cyan-500 bg-cyan-50' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            🔍 Detections {otherThreats.length > 0 && `(${otherThreats.length})`}
          </button>
          <button
            onClick={() => setActiveTab('actions')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'actions' 
                ? 'text-purple-600 border-b-2 border-purple-500 bg-purple-50' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            ⚡ Actions {vpnActions.length > 0 && `(${vpnActions.length})`}
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'events' 
                ? 'text-cyan-600 border-b-2 border-cyan-500 bg-cyan-50' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            📊 Events
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {activeTab === 'detections' ? (
            otherThreats.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-3xl">✅</span>
                <p className="text-slate-700 font-medium mt-4">All Clear</p>
                <p className="text-slate-500 text-sm mt-1">No threat patterns detected</p>
              </div>
            ) : (
              otherThreats.map(threat => (
                <div key={threat.id} className={`border-2 rounded-xl p-4 ${
                  threat.type === 'ISOLATION' ? 'border-red-400 bg-red-50' : 'border-yellow-400 bg-yellow-50'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{threat.type === 'ISOLATION' ? '🛡️' : '⚠️'}</span>
                      <div>
                        <div className="text-slate-800 font-semibold text-sm">{threat.type}</div>
                        <div className="text-slate-500 text-xs">Heuristic Pattern</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-400 text-[10px]">Confidence</div>
                      <div className="text-slate-800 font-bold">{threat.confidence}%</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <span className="font-mono text-sm bg-white px-2 py-1 rounded text-cyan-700 border border-slate-200">{threat.device}</span>
                  </div>
                  <div>
                    <div className="text-slate-800 text-sm font-medium">{threat.reason}</div>
                    <div className="text-slate-500 text-xs mt-1">
                      Targeting: {threat.targetDevices?.slice(0, 3).join(', ')}{threat.targetDevices?.length > 3 ? ` +${threat.targetDevices.length - 3} more` : ''}
                    </div>
                  </div>
                </div>
              ))
            )
          ) : activeTab === 'actions' ? (
            vpnActions.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-3xl">⚡</span>
                <p className="text-slate-700 font-medium mt-4">No Pending Actions</p>
                <p className="text-slate-500 text-sm mt-1">AI Sentry will recommend actions when threats are detected</p>
              </div>
            ) : (
              vpnActions.map(action => {
                const result = actionResults[action.id];
                const isExecuting = executingAction === action.id;
                
                return (
                  <div key={action.id} className="border-2 border-purple-400 bg-purple-50 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🔒</span>
                        <div>
                          <div className="text-slate-800 font-semibold text-sm">Network Response</div>
                          <div className="text-purple-600 text-xs">Nimbus API Action</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-slate-400 text-[10px]">Confidence</div>
                        <div className="text-slate-800 font-bold">{action.confidence}%</div>
                      </div>
                    </div>
                    
                    <div className="mb-3 p-2 bg-white rounded-lg border border-slate-200">
                      <div className="text-[10px] text-slate-500 uppercase mb-1">Target Router</div>
                      <div className="font-mono text-sm text-cyan-700">{action.agentId || 'Stratus5G_5728_Austin'}</div>
                    </div>
                    
                    <div className="mb-3 p-3 bg-white rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🏷️</span>
                        <span className="text-slate-800 text-sm font-medium">Security Tag Assignment</span>
                      </div>
                      <div className="bg-slate-50 rounded px-3 py-2 font-mono text-sm text-purple-700 border border-slate-200">
                        aisentry-isolated-{new Date().toISOString().slice(0,10).replace(/-/g,'')}
                      </div>
                      <div className="text-slate-500 text-xs mt-2 flex items-center gap-1">
                        <span>👁️</span> Visible in Nimbus Portal
                      </div>
                    </div>
                    
                    <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <div className="text-xs text-red-600 font-medium mb-1">Responding to Threat:</div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-red-700">{action.device}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-slate-600 text-sm">{action.targetDevices?.length || 0} PLCs targeted</span>
                      </div>
                    </div>
                    
                    {result && (
                      <div className={`mb-3 p-3 rounded-lg ${result.success ? 'bg-emerald-50 border border-emerald-300' : 'bg-red-50 border border-red-300'}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{result.success ? '✅' : '❌'}</span>
                          <div>
                            <div className={`text-sm font-medium ${result.success ? 'text-emerald-700' : 'text-red-700'}`}>
                              {result.success ? 'Tag Applied Successfully!' : `Failed: ${result.error}`}
                            </div>
                            {result.success && result.data?.tag_name && (
                              <div className="text-emerald-600 text-xs mt-0.5 font-mono">{result.data.tag_name}</div>
                            )}
                          </div>
                        </div>
                        {result.success && (
                          <p className="text-emerald-600 text-xs mt-2">✨ Check Nimbus portal - tag should appear on router</p>
                        )}
                      </div>
                    )}
                    
                    {!result?.success && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApproveVPN(action)}
                          disabled={isExecuting}
                          className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 disabled:from-slate-400 disabled:to-slate-400 text-white text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                        >
                          {isExecuting ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Sending to Nimbus...
                            </>
                          ) : (
                            <>
                              <span>✓</span>
                              Approve & Execute
                            </>
                          )}
                        </button>
                        <button
                          disabled={isExecuting}
                          className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-600 text-sm font-medium rounded-lg transition-colors border border-slate-200"
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )
          ) : (
            <>
              {criticalEvents.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    Critical Events ({criticalEvents.length})
                  </h3>
                  <div className="space-y-2">
                    {criticalEvents.map((event, idx) => (
                      <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-xs text-red-700 font-medium">{event.details?.policy_name || 'Policy Match'}</span>
                          <span className="text-[10px] text-slate-500">
                            {event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-slate-600">{event.source_ip || 'N/A'}</span>
                          <span className="text-slate-400">→</span>
                          <span className="font-mono text-cyan-700">{event.destination_ip || 'N/A'}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1 truncate">
                          {event.description || event.details?.rule_name || 'Security event detected'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {warningEvents.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-xs font-semibold text-yellow-600 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full" />
                    Warning Events ({warningEvents.length})
                  </h3>
                  <div className="space-y-2">
                    {warningEvents.map((event, idx) => (
                      <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-xs text-yellow-700 font-medium">{event.details?.policy_name || 'Policy Match'}</span>
                          <span className="text-[10px] text-slate-500">
                            {event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-slate-600">{event.source_ip || 'N/A'}</span>
                          <span className="text-slate-400">→</span>
                          <span className="font-mono text-cyan-700">{event.destination_ip || 'N/A'}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1 truncate">
                          {event.description || event.details?.rule_name || 'Security event detected'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {criticalEvents.length === 0 && warningEvents.length === 0 && (
                <div className="text-center py-12">
                  <span className="text-3xl">📭</span>
                  <p className="text-slate-700 font-medium mt-4">No Recent Alerts</p>
                  <p className="text-slate-500 text-sm mt-1">Click refresh to fetch latest events</p>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between flex-shrink-0 bg-slate-50">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gradient-to-br from-cyan-500 to-blue-500 rounded flex items-center justify-center">
              <span className="text-[10px] font-bold text-white">e</span>
            </div>
            <span className="text-xs text-slate-500">Powered by <span className="text-cyan-600">AI Sentry</span></span>
          </div>
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="text-xs text-cyan-600 hover:text-cyan-700 disabled:opacity-50"
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>
      </div>
    </>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// EDGE NODE FILTER - LIGHT MODE
// ════════════════════════════════════════════════════════════════════════════

const EdgeNodeFilter = ({ edgeNodes, selectedAgent, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedNode = selectedAgent ? edgeNodes.find(n => n.edge_node_id === selectedAgent) : null;
  
  return (
    <div className="relative">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg px-4 py-2 text-sm text-slate-700 min-w-[280px] shadow-sm">
        <svg className="w-4 h-4 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        <span className="flex-1 text-left truncate">{selectedNode ? selectedNode.edge_node_id : 'All Edge Nodes'}</span>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
            <button onClick={() => { onSelect(null); setIsOpen(false); }} className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-50 ${!selectedAgent ? 'bg-cyan-50 text-cyan-700' : 'text-slate-700'}`}>
              All Edge Nodes
            </button>
            {[...edgeNodes].sort((a, b) => (a.edge_node_id || '').localeCompare(b.edge_node_id || '')).map(node => {
              const isOnline = isNodeOnline(node);
              const status = isOnline ? 'Online' : 'Offline';
              return (
                <button key={node.edge_node_id} onClick={() => { onSelect(node.edge_node_id); setIsOpen(false); }} className={`w-full px-4 py-3 text-left hover:bg-slate-50 ${selectedAgent === node.edge_node_id ? 'bg-cyan-50' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <span className={`text-sm font-medium ${selectedAgent === node.edge_node_id ? 'text-cyan-700' : 'text-slate-800'}`}>{node.edge_node_id}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ml-auto ${isOnline ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{status}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR - LIGHT MODE
// ════════════════════════════════════════════════════════════════════════════

const StatsBar = ({ stats, threatsCount, onOpenPanel, onRequestBriefing }) => (
  <div className="flex items-center gap-4">
    <button
      onClick={onRequestBriefing}
      className="flex items-center gap-2 bg-gradient-to-r from-cyan-50 to-blue-50 hover:from-cyan-100 hover:to-blue-100 border border-cyan-300 rounded-lg px-4 py-2 transition-all"
    >
      <span className="text-lg">📋</span>
      <span className="text-sm font-medium text-cyan-700">Briefing</span>
    </button>
    
    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200 shadow-sm">
      <span className="text-emerald-600 font-bold text-lg">{stats.onlineAgents || 0}</span>
      <span className="text-slate-500 text-xs">/ {stats.totalAgents || 0} Online</span>
    </div>
    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200 shadow-sm">
      <span className="text-blue-600 font-bold text-lg">{stats.totalDevices || 0}</span>
      <span className="text-slate-500 text-xs">Devices</span>
    </div>
    {(stats.alertCount || 0) > 0 && (
      <div className="flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2 border border-red-200 animate-pulse">
        <span className="text-red-600 font-bold text-lg">{stats.alertCount}</span>
        <span className="text-red-500 text-xs">Alerts</span>
      </div>
    )}
    <button onClick={onOpenPanel} className={`flex items-center gap-2 rounded-lg px-4 py-2 border transition-all shadow-sm ${threatsCount > 0 ? 'bg-purple-50 border-purple-300 hover:bg-purple-100' : 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100'}`}>
      <span className="text-lg">🤖</span>
      <div className="text-left">
        <div className={`font-bold text-sm ${threatsCount > 0 ? 'text-purple-700' : 'text-emerald-700'}`}>AI Sentry</div>
        <div className={`text-[10px] ${threatsCount > 0 ? 'text-purple-600' : 'text-emerald-600'}`}>{threatsCount > 0 ? `${threatsCount} detections` : 'All clear'}</div>
      </div>
      {threatsCount > 0 && (
        <span className="w-5 h-5 bg-purple-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">{threatsCount}</span>
      )}
    </button>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// LEGEND - LIGHT MODE
// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// CONTEXT-AWARE CHAT PANEL - Single Pane of Glass
// ════════════════════════════════════════════════════════════════════════════

// Helper to create a new chat session
const createNewSession = (idSuffix = '') => {
  const timestamp = Date.now();
  return {
    id: `sentry-${timestamp}${idSuffix}`,
    title: `Chat ${new Date(timestamp).toLocaleDateString([], { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
    messages: [],
    createdAt: timestamp,
  };
};

const STORAGE_KEY = 'aiSentryChatSessions';
const CURRENT_SESSION_KEY = 'aiSentryCurrentSession';

const ChatPanel = ({ threats, stats, selectedAgent }) => {
  // Session state
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Chat state
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const messagesEndRef = useRef(null);
  const dropdownRef = useRef(null);
  const MIN_INTERVAL = 10000;

  // ═══════════════════════════════════════════════════════════
  // LOCALSTORAGE INITIALIZATION
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    let initialSessions = [];
    let initialCurrentId = null;
    
    try {
      const storedSessions = localStorage.getItem(STORAGE_KEY);
      const storedCurrentId = localStorage.getItem(CURRENT_SESSION_KEY);
      
      if (storedSessions) {
        initialSessions = JSON.parse(storedSessions);
      }
      
      if (initialSessions.length > 0) {
        if (storedCurrentId && initialSessions.find(s => s.id === storedCurrentId)) {
          initialCurrentId = storedCurrentId;
        } else {
          initialSessions.sort((a, b) => b.createdAt - a.createdAt);
          initialCurrentId = initialSessions[0].id;
        }
      } else {
        const newSession = createNewSession('-initial');
        initialSessions = [newSession];
        initialCurrentId = newSession.id;
      }
    } catch (error) {
      console.error("Error loading chat sessions:", error);
      const newSession = createNewSession('-fallback');
      initialSessions = [newSession];
      initialCurrentId = newSession.id;
    }
    
    setSessions(initialSessions);
    setCurrentSessionId(initialCurrentId);
  }, []);

  // ═══════════════════════════════════════════════════════════
  // DEBOUNCED LOCALSTORAGE WRITES
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (sessions.length > 0 && currentSessionId) {
      const timeoutId = setTimeout(() => {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
          localStorage.setItem(CURRENT_SESSION_KEY, currentSessionId);
        } catch (error) {
          console.error("Error saving chat sessions:", error);
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [sessions, currentSessionId]);

  // ═══════════════════════════════════════════════════════════
  // DERIVED STATE
  // ═══════════════════════════════════════════════════════════
  const currentSession = useMemo(() => {
    return sessions.find(s => s.id === currentSessionId);
  }, [sessions, currentSessionId]);

  const messages = useMemo(() => {
    return currentSession ? currentSession.messages : [];
  }, [currentSession]);

  // Sort sessions for dropdown
  const sortedSessions = useMemo(() => {
    return [...sessions].sort((a, b) => b.createdAt - a.createdAt);
  }, [sessions]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ═══════════════════════════════════════════════════════════
  // SESSION MANAGEMENT
  // ═══════════════════════════════════════════════════════════
  const handleNewChat = () => {
    const newSession = createNewSession();
    setSessions(prev => [newSession, ...prev].sort((a, b) => b.createdAt - a.createdAt));
    setCurrentSessionId(newSession.id);
    setInput('');
    setIsDropdownOpen(false);
  };

  const handleSelectSession = (sessionId) => {
    setCurrentSessionId(sessionId);
    setInput('');
    setIsDropdownOpen(false);
  };

  const handleDeleteSession = (sessionIdToDelete, e) => {
    e.stopPropagation();
    setSessions(prev => {
      const remaining = prev.filter(s => s.id !== sessionIdToDelete);
      if (remaining.length === 0) {
        const newSession = createNewSession('-after-delete');
        setCurrentSessionId(newSession.id);
        return [newSession];
      }
      if (currentSessionId === sessionIdToDelete) {
        const sorted = [...remaining].sort((a, b) => b.createdAt - a.createdAt);
        setCurrentSessionId(sorted[0].id);
      }
      return remaining;
    });
  };

  // ═══════════════════════════════════════════════════════════
  // CHAT FUNCTIONS
  // ═══════════════════════════════════════════════════════════
  const buildContext = () => {
    let context = `Current Network State:\n`;
    context += `- Agents: ${stats.onlineAgents}/${stats.totalAgents} online\n`;
    context += `- Devices: ${stats.totalDevices}\n`;
    context += `- Alerts: ${stats.alertCount}\n`;
    if (selectedAgent) context += `- Viewing: ${selectedAgent}\n`;
    
    if (threats.length > 0) {
      context += `\nActive Threats:\n`;
      threats.slice(0, 5).forEach(t => {
        context += `- ${t.type}: ${t.device} (${t.confidence}% confidence) - ${t.reason}\n`;
      });
    }
    return context;
  };

  const canSend = () => !isLoading && input.trim() && (Date.now() - lastRequestTime >= MIN_INTERVAL);
  const getWaitTime = () => Math.max(0, Math.ceil((MIN_INTERVAL - (Date.now() - lastRequestTime)) / 1000));

  const handleSend = async () => {
    if (!canSend() || !currentSessionId) return;

    const userMessage = input.trim();
    const sessionToUpdate = sessions.find(s => s.id === currentSessionId);
    if (!sessionToUpdate) return;

    // Update title on first message
    let newTitle = sessionToUpdate.title;
    if (sessionToUpdate.messages.length === 0 && userMessage) {
      newTitle = userMessage.length > 20 
        ? `${userMessage.substring(0, 20)}...` 
        : userMessage;
    }

    const userMsg = { role: 'user', content: userMessage };
    
    setSessions(prev => prev.map(s => 
      s.id === currentSessionId 
        ? { ...s, messages: [...s.messages, userMsg], title: newTitle }
        : s
    ));
    
    setInput('');
    setIsLoading(true);
    setLastRequestTime(Date.now());

    try {
      const contextualQuery = `${buildContext()}\n\nUser question: ${userMessage}`;
      const history = sessionToUpdate.messages.slice(-6).map(m => ({ role: m.role, content: m.content }));
      
      const response = await sendChatQuery(contextualQuery, history, currentSessionId);
      const assistantMsg = { role: 'assistant', content: response.content || 'No response received.' };
      
      setSessions(prev => prev.map(s => 
        s.id === currentSessionId 
          ? { ...s, messages: [...s.messages, assistantMsg] }
          : s
      ));
    } catch (error) {
      const errorMsg = { role: 'assistant', content: `Error: ${error.message}` };
      setSessions(prev => prev.map(s => 
        s.id === currentSessionId 
          ? { ...s, messages: [...s.messages, errorMsg] }
          : s
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickAction = (query) => {
    setInput(query);
    setTimeout(() => handleSend(), 100);
  };

  return (
    <div 
      className="bg-white border border-slate-200 rounded-xl shadow-xl flex flex-col"
      style={{ width: '476px', height: '416px', position: 'relative', overflow: 'hidden' }}
    >
      {/* Header with Session Dropdown */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">🤖</span>
            <div>
              <div className="text-sm font-semibold text-slate-800">Ask AI Sentry</div>
              <div className="text-[10px] text-slate-500">
                {threats.length} threats • {sessions.length} chat{sessions.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          
          {/* Session Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-1.5 px-2 py-1 text-[10px] bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <span className="text-slate-600 truncate max-w-[80px]">
                {currentSession?.title || 'Chat'}
              </span>
              <svg className={`w-3 h-3 text-slate-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {isDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden">
                <button
                  onClick={handleNewChat}
                  className="w-full px-3 py-2 text-left text-xs text-cyan-700 font-medium hover:bg-cyan-50 flex items-center gap-2 border-b border-slate-100"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Chat
                </button>
                <div className="max-h-40 overflow-y-auto">
                  {sortedSessions.map(session => (
                    <div
                      key={session.id}
                      onClick={() => handleSelectSession(session.id)}
                      className={`px-3 py-2 text-xs cursor-pointer flex items-center justify-between group ${
                        session.id === currentSessionId 
                          ? 'bg-cyan-50 text-cyan-700' 
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <span className="truncate flex-1 mr-2">{session.title}</span>
                      <button
                        onClick={(e) => handleDeleteSession(session.id, e)}
                        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 p-0.5"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3" style={{ minHeight: 0 }}>
        {messages.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-slate-500 text-xs mb-3">Ask about threats, devices, or security status</p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {threats.length > 0 && (
                <button
                  onClick={() => handleQuickAction(`What's happening with ${threats[0].device}?`)}
                  className="px-2 py-1 text-[10px] bg-red-50 text-red-700 rounded-full hover:bg-red-100 border border-red-200"
                >
                  About {threats[0].device}
                </button>
              )}
              <button
                onClick={() => handleQuickAction("Summarize current threats")}
                className="px-2 py-1 text-[10px] bg-cyan-50 text-cyan-700 rounded-full hover:bg-cyan-100 border border-cyan-200"
              >
                Threat Summary
              </button>
              <button
                onClick={() => handleQuickAction("What actions should I take?")}
                className="px-2 py-1 text-[10px] bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100 border border-purple-200"
              >
                Recommended Actions
              </button>
            </div>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'user' ? (
                <div className="bg-blue-600 text-white px-3 py-2 rounded-xl rounded-br-sm max-w-[85%] text-xs">
                  {msg.content}
                </div>
              ) : (
                <div className="bg-slate-100 border border-slate-200 px-3 py-2 rounded-xl rounded-bl-sm max-w-[85%]">
                  <div className="text-xs text-slate-700 prose prose-xs max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({children}) => <p className="mb-1.5 last:mb-0 text-xs">{children}</p>,
                        ul: ({children}) => <ul className="list-disc pl-3 mb-1.5 space-y-0.5 text-xs">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal pl-3 mb-1.5 space-y-0.5 text-xs">{children}</ol>,
                        li: ({children}) => <li className="text-xs">{children}</li>,
                        strong: ({children}) => <strong className="font-semibold text-slate-900">{children}</strong>,
                        code: ({children}) => <code className="px-1 py-0.5 bg-slate-200 text-cyan-700 rounded text-[10px]">{children}</code>,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex items-center gap-2 text-slate-500 text-xs">
            <div className="flex space-x-1">
              <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span>Thinking...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 p-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about this network..."
            className="flex-1 px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!canSend()}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              canSend()
                ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? '...' : '→'}
          </button>
        </div>
        {lastRequestTime > 0 && getWaitTime() > 0 && (
          <div className="text-[10px] text-orange-600 mt-1 text-center">
            Wait {getWaitTime()}s
          </div>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT - LIGHT MODE
// ════════════════════════════════════════════════════════════════════════════

const AISentryLight = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edgeNodesList, setEdgeNodesList] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [threats, setThreats] = useState([]);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [showBriefing, setShowBriefing] = useState(true);
  const [aiBriefing, setAiBriefing] = useState(null);
  const [expandedBriefing, setExpandedBriefing] = useState(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [isLoadingExpanded, setIsLoadingExpanded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stats, setStats] = useState({ totalAgents: 0, onlineAgents: 0, totalDevices: 0, alertCount: 0 });

  const fetchBriefing = useCallback(async () => {
    setIsLoadingAI(true);
    const briefing = await generateAIBriefing();
    setAiBriefing(briefing);
    setIsLoadingAI(false);
  }, []);

  const fetchExpandedBriefing = useCallback(async () => {
    setIsLoadingExpanded(true);
    const expanded = await generateExpandedBriefing();
    setExpandedBriefing(expanded);
    setIsLoadingExpanded(false);
  }, []);

  const handleRequestBriefing = useCallback(() => {
    setShowBriefing(true);
    if (!aiBriefing) fetchBriefing();
  }, [aiBriefing, fetchBriefing]);

  const buildTopology = useCallback((edgeNodes, events, filterAgentId, currentThreats) => {
    const newNodes = [];
    const newEdges = [];
    const filteredEdgeNodes = filterAgentId ? edgeNodes.filter(n => n.edge_node_id === filterAgentId) : edgeNodes;
    
    const CLOUD_Y = 0;
    const EDGE_Y = 200;
    const DEVICE_Y_START = 420;
    const HORIZONTAL_GAP = filteredEdgeNodes.length > 4 ? 260 : filteredEdgeNodes.length > 3 ? 300 : 350;
    const DEVICE_GAP = 180;
    
    const devicesByAgent = {};
    let totalAlerts = 0;
    
    events.forEach(event => {
      const agentId = event.celerway_edge_node_id;
      const destIp = event.destination_ip;
      if (!agentId || !destIp) return;
      if (filterAgentId && agentId !== filterAgentId) return;
      
      if (!devicesByAgent[agentId]) devicesByAgent[agentId] = {};
      if (!devicesByAgent[agentId][destIp]) {
        devicesByAgent[agentId][destIp] = { ip: destIp, protocols: new Set(), eventCount: 0, policyViolations: 0, role: 'PLC' };
      }
      
      const device = devicesByAgent[agentId][destIp];
      device.eventCount++;
      if (event.protocol) device.protocols.add(event.protocol.toLowerCase());
      if (event.severity === 'Warning' || event.severity === 'Critical') {
        device.policyViolations++;
        totalAlerts++;
      }
    });
    
    let totalDevices = 0;
    Object.values(devicesByAgent).forEach(devices => { totalDevices += Object.keys(devices).length; });
    
    const totalWidth = Math.max(filteredEdgeNodes.length * HORIZONTAL_GAP, 600);
    const startX = -totalWidth / 2 + HORIZONTAL_GAP / 2;
    
    newNodes.push({
      id: 'cloud',
      type: 'cloud',
      position: { x: 0, y: CLOUD_Y },
      data: { label: 'AI Sentry Cloud', sublabel: 'ai-sentry.com', edgeCount: filteredEdgeNodes.length, assetCount: totalDevices },
    });
    
    filteredEdgeNodes.forEach((edgeNode, index) => {
      const edgeX = startX + index * HORIZONTAL_GAP;
      const nodeId = edgeNode.edge_node_id;
      const status = getNodeStatus(edgeNode);
      const agentDevices = devicesByAgent[nodeId] || {};
      const deviceCount = Object.keys(agentDevices).length;
      
      newNodes.push({
        id: `edge-${nodeId}`,
        type: 'edgeGateway',
        position: { x: edgeX, y: EDGE_Y },
        data: { nodeId, hostname: edgeNode.hostname, status, ipAddress: edgeNode.ip_address, version: edgeNode.agent_version, lastHeartbeat: edgeNode.last_heartbeat, deviceCount },
      });
      
      newEdges.push({
        id: `cloud-to-${nodeId}`,
        source: 'cloud',
        target: `edge-${nodeId}`,
        animated: status === 'Online',
        style: { stroke: status === 'Online' ? '#06b6d4' : '#ef4444', strokeWidth: 2 },
      });
      
      const deviceList = Object.values(agentDevices);
      const deviceStartX = edgeX - ((deviceList.length - 1) * DEVICE_GAP) / 2;
      
      deviceList.forEach((device, deviceIndex) => {
        const deviceX = deviceStartX + deviceIndex * DEVICE_GAP;
        const deviceId = `device-${nodeId}-${device.ip}`;
        const aiStatus = getDeviceAIStatus(device.ip, currentThreats || []);
        
        newNodes.push({
          id: deviceId,
          type: 'device',
          position: { x: deviceX, y: DEVICE_Y_START + Math.random() * 40 },
          data: { ip: device.ip, protocols: Array.from(device.protocols), eventCount: device.eventCount, policyViolations: device.policyViolations, role: device.role, aiStatus },
        });
        
        let edgeColor = '#60a5fa';
        if (aiStatus === AI_STATUS.ISOLATED) edgeColor = '#ef4444';
        else if (aiStatus === AI_STATUS.ANALYZING) edgeColor = '#a855f7';
        else if (device.policyViolations > 0) edgeColor = '#f59e0b';
        
        newEdges.push({
          id: `${nodeId}-to-${device.ip}`,
          source: `edge-${nodeId}`,
          target: deviceId,
          animated: true,
          style: { stroke: edgeColor, strokeWidth: 2 },
        });
      });
    });
    
    return {
      nodes: newNodes,
      edges: newEdges,
      stats: { totalAgents: filteredEdgeNodes.length, onlineAgents: filteredEdgeNodes.filter(n => isNodeOnline(n)).length, totalDevices, alertCount: totalAlerts }
    };
  }, []);

  const handleManualRefresh = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const [edgeNodesResponse, eventsResponse] = await Promise.all([
        edgeNodesService.getEdgeNodes(),
        eventsService.getEvents({ limit: 500 }),
      ]);
      
      const edgeNodes = edgeNodesResponse.data || edgeNodesResponse || [];
      const events = eventsResponse.data || eventsResponse || [];
      
      setEdgeNodesList(edgeNodes);
      setAllEvents(events);
      
      const newThreats = detectThreatsHeuristic(events);
      setThreats(newThreats);
      
      const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodes, events, selectedAgent, newThreats);
      setNodes(newNodes);
      setEdges(newEdges);
      setStats(newStats);
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [buildTopology, setNodes, setEdges, selectedAgent]);

  const handleApproveVPN = useCallback(async (action) => {
    try {
      const result = await executeNimbusAction('vpn_profile_change', {
        agent_id: action.agentId || 'Stratus5G_5728_Austin',
        current_profile: action.actionDetails?.currentProfile || 'Standard',
        new_profile: action.actionDetails?.recommendedProfile || 'Isolated-Secure',
        threat_source: action.device,
        target_devices: action.targetDevices,
        reason: action.reason
      });
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [edgeNodesResponse, eventsResponse] = await Promise.all([
        edgeNodesService.getEdgeNodes(),
        eventsService.getEvents({ limit: 500 }),
      ]);
      
      const edgeNodes = edgeNodesResponse.data || edgeNodesResponse || [];
      const events = eventsResponse.data || eventsResponse || [];
      
      setEdgeNodesList(edgeNodes);
      setAllEvents(events);
      
      const newThreats = detectThreatsHeuristic(events);
      setThreats(newThreats);
      
      const agentToUse = selectedAgent || (edgeNodes.length > 0 ? edgeNodes[4].edge_node_id : null);
      if (!selectedAgent && edgeNodes.length > 0) setSelectedAgent(agentToUse);
      
      const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodes, events, agentToUse, newThreats);
      
      setNodes(newNodes);
      setEdges(newEdges);
      setStats(newStats);
      
      if (showBriefing && !aiBriefing) fetchBriefing();
      
    } catch (err) {
      console.error('Failed to fetch:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buildTopology, setNodes, setEdges, selectedAgent, showBriefing, aiBriefing, fetchBriefing]);

  useEffect(() => {
    if (edgeNodesList.length > 0) {
      const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodesList, allEvents, selectedAgent, threats);
      setNodes(newNodes);
      setEdges(newEdges);
      setStats(newStats);
    }
  }, [selectedAgent, edgeNodesList, allEvents, threats, buildTopology, setNodes, setEdges]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 180000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-white relative overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />
      </div>
      
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-white via-white to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl flex items-center justify-center border border-cyan-300">
                <span className="text-xl">🤖</span>
              </span>
              AI Sentry
            </h1>
            <p className="text-slate-500 text-sm mt-1">Autonomous Security • Real-time AI Analysis • Proactive Protection</p>
            <div className="mt-3">
              <EdgeNodeFilter edgeNodes={edgeNodesList} selectedAgent={selectedAgent} onSelect={setSelectedAgent} />
            </div>
          </div>
          <StatsBar stats={stats} threatsCount={threats.length} onOpenPanel={() => setIsAIPanelOpen(true)} onRequestBriefing={handleRequestBriefing} />
        </div>
      </div>
      
      {/* Shift Briefing Modal */}
      <ShiftBriefingModal 
        isOpen={showBriefing && !loading}
        onClose={() => setShowBriefing(false)}
        aiBriefing={aiBriefing}
        stats={stats}
        threats={threats}
        isLoadingAI={isLoadingAI}
        onRequestExpanded={fetchExpandedBriefing}
        expandedBriefing={expandedBriefing}
        isLoadingExpanded={isLoadingExpanded}
      />
      
      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-200 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-cyan-600 text-lg font-medium">AI Sentry Initializing...</div>
          </div>
        </div>
      )}
      
      {/* Error */}
      {error && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/90">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <div className="text-red-600 text-lg">Failed to load: {error}</div>
          </div>
        </div>
      )}
      
      {/* React Flow */}
      {!loading && !error && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.4 }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(0, 0, 0, 0.05)" />
          <Controls className="!bg-white !border-slate-200 !rounded-xl !shadow-lg" />
        </ReactFlow>
      )}
      
      {/* Chat Panel - Fixed position outside ReactFlow */}
      {!loading && !error && (
        <div className="absolute bottom-4 left-4 z-30">
          <ChatPanel threats={threats} stats={stats} selectedAgent={selectedAgent} />
        </div>
      )}
      
      {/* AI Panel */}
      <AIPanel 
        isOpen={isAIPanelOpen} 
        onClose={() => setIsAIPanelOpen(false)} 
        threats={threats}
        recentEvents={allEvents}
        onRefresh={handleManualRefresh}
        isRefreshing={isRefreshing}
        onApproveVPN={handleApproveVPN}
      />
      
      <style>{`
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default AISentryLight;
